// DEITY AVATAR ROLLOUT — Phase 1 registry (docs/DEITY_AVATAR_PLAN.md).
//
// Single source of truth driving every per-tool deity interface: name, gear
// color, GLB url (with fallback until real models land), voice id, the
// server-side table allowlist (enforced in Phase 2's /api/ai/deity route,
// NOT here — this is just data), and tutorial/prompt-menu placeholders
// (Phase 4/5).
//
// Ids are the canonical DMT ids from spectrum-tools.ts (DmtToolId) — the same
// ids deity-data.ts and ascension.ts key by. NOTE: two of those DMT ids
// ('astra', 'janus') are NOT the ToolHost screen keys for those tools —
// ToolHost registers the actual mountable screens as 'astrs' (spine editor)
// and 'jnus' (decision tool) instead (see ToolHost.tsx NATIVE_TOOLS; this is
// a pre-existing legacy naming split, not something Phase 1 fixes).
// TOOLID_ALIASES below bridges ToolHost's screen id to this registry's
// canonical DMT id so `getDeityAvatar()` works with whatever id the shell
// passes in.
import { GEAR_TIERS, type GearId } from './ascension';
import { AVATAR_GLB_URL } from './avatarScene';
import { DEITY_DATA } from './deity-data';
import { ORDER_SECTION_META } from './orderSections';
import { DMT_TOOLS, type DmtToolId } from './spectrum-tools';

const API_BASE = process.env.EXPO_PUBLIC_API_BASE_URL ?? '';

export interface PromptMenuItem {
  label: string;
  /** Pre-recorded clip-set id (Phase 4/5) — no runtime AI cost. */
  clipSet: string;
}

export interface DeityAvatarDef {
  toolId: DmtToolId | 'syqi';
  deityName: string;
  gear: GearId | null;
  /** `${API_BASE}/avatars/{toolId}.glb` — not necessarily reachable yet; see glbReady. */
  glbUrl: string;
  /** TTS voice id. Placeholder = toolId; distinct per-deity voices are a later phase. */
  voice: string;
  /** Gear band color, from orderSections' ORDER_SECTION_META. */
  color: string;
  /** Deity persona + tool purpose stub — Phase 2 loads the real prompt server-side. */
  systemPrompt: string;
  /** Hard allowlist of tables this deity's agent may touch (server-enforced in Phase 2). */
  tables: { read: string[]; write: string[] };
  /** Pre-recorded first-use tutorial clip set (Phase 4). */
  intro: { videoClipSet: string };
  /** Prompt-menu chips (Phase 5 content; disabled placeholders in Phase 1 UI). */
  promptMenu: PromptMenuItem[];
  /** True once a real per-deity GLB is hosted. All false in Phase 1. */
  glbReady: boolean;
}

// Per-tool table map — DISPLAY-ONLY. Corrected 2026-07-25 per
// docs/DEITY_AGENT_BACKEND_DESIGN.md §0/§7 follow-up: the previous list here
// named several nonexistent tables (hrmez_state, hrmez_logs, aggregates,
// argos_logs, argos_state). Real table names come from
// src/lib/backup/tables.ts (read-only reference, not imported here to avoid
// a mobile->root src/ dependency). The SERVER-side allowlist in
// src/lib/agents/deity-config.ts is the only enforced/authoritative source —
// this map is never sent to or trusted by the /api/ai/deity route; it only
// drives this file's now-unused-by-the-route `tables` field on DeityAvatarDef
// (kept for any future display use, e.g. "what can this deity see").
const TABLE_MAP: Record<DmtToolId, string[]> = {
  telos: ['telos_state'],
  oracle: ['telos_state'],
  astra: ['projects'],
  moyri: ['moyri_logs'],
  numa: ['tasks', 'task_lists', 'agenda_items'],
  hrmez: [
    'hrmez_config', 'hrmez_rules', 'hrmez_decisions', 'hrmez_entries',
    'hrmez_classifications', 'hrmez_ambiguous', 'hrmez_reports',
    'hrmez_local_files', 'hrmez_local_folders',
  ],
  kyrs: ['tasks', 'task_lists'],
  krnos: ['calendar_events', 'calendars'],
  tkne: ['designs'],
  metis: ['agenda_items'],
  themis: ['protocols', 'protocol_items', 'protocol_completions', 'protocol_schedules'],
  janus: ['jnus_decisions'],
  clio: ['journal_entries', 'notes', 'note_folders'],
  argos: ['argos_categories'],
  nemos: ['recalls', 'notes', 'note_folders', 'sounds'],
};

function gearOf(toolId: DmtToolId): GearId {
  for (const gear of Object.keys(GEAR_TIERS) as GearId[]) {
    const tiers = GEAR_TIERS[gear];
    if (tiers.mortal === toolId || tiers.titan === toolId || tiers.olympian === toolId) return gear;
  }
  // Should be unreachable for any id in DMT_TOOLS — fall back to 'orient'.
  return 'orient';
}

function buildSystemPromptStub(toolId: DmtToolId, deityName: string): string {
  const info = DEITY_DATA[toolId];
  const ability = info?.ability ?? 'help the user use this tool';
  const have = info?.have ?? '';
  return (
    `You are ${deityName}, the deity of this tool. You have ${ability} ` +
    `${have} Speak in character, stay concise, and only ever read/write the ` +
    `tables you've been granted for this tool — never guess at other data. ` +
    `(Phase 1 stub — Phase 2's /api/ai/deity route replaces this with the real, ` +
    `server-loaded persona + tool manifest.)`
  ).trim();
}

function buildEntry(toolId: DmtToolId): DeityAvatarDef {
  const dmt = DMT_TOOLS.find((t) => t.id === toolId);
  const deityName = dmt?.name ?? toolId.toUpperCase();
  const gear = gearOf(toolId);
  const tables = TABLE_MAP[toolId] ?? [];
  return {
    toolId,
    deityName,
    gear,
    glbUrl: `${API_BASE}/avatars/${toolId}.glb`,
    voice: toolId,
    color: ORDER_SECTION_META[gear].color,
    systemPrompt: buildSystemPromptStub(toolId, deityName),
    tables: { read: tables, write: tables },
    intro: { videoClipSet: `intro_${toolId}` },
    promptMenu: [
      { label: 'What is this tool?', clipSet: `intro_${toolId}_what` },
      { label: 'How do I use it?', clipSet: `intro_${toolId}_how` },
      { label: 'Just do it for me', clipSet: `intro_${toolId}_doit` },
    ],
    glbReady: false,
  };
}

const DMT_ENTRIES: Record<DmtToolId, DeityAvatarDef> = Object.fromEntries(
  DMT_TOOLS.map((t) => [t.id, buildEntry(t.id)])
) as Record<DmtToolId, DeityAvatarDef>;

// SyQi itself — included per the plan's "15 deities + syqi" registry, but
// note SyqiTool/SyqiHome already IS the model interface (its own overview
// panel + avatar); the eye-toggle wiring in ToolPanel deliberately skips
// 'syqi' so that screen stays untouched.
const SYQI_ENTRY: DeityAvatarDef = {
  toolId: 'syqi',
  deityName: 'SyQi',
  gear: null,
  glbUrl: AVATAR_GLB_URL,
  voice: 'syqi',
  color: '#2f80ff',
  systemPrompt: 'You are SyQi, the user\'s whole-system guide across every gear.',
  tables: { read: [], write: [] },
  intro: { videoClipSet: 'intro_syqi' },
  promptMenu: [],
  glbReady: true,
};

export const DEITY_AVATARS: Record<DmtToolId | 'syqi', DeityAvatarDef> = {
  ...DMT_ENTRIES,
  syqi: SYQI_ENTRY,
};

// ToolHost screen id -> canonical registry id, for the two ids that diverge
// (see file header comment).
const TOOLID_ALIASES: Record<string, DmtToolId | 'syqi'> = {
  astrs: 'astra',
  jnus: 'janus',
};

/** Resolves any ToolHost screen id (or a canonical DMT id) to its registry entry. */
export function getDeityAvatar(toolId: string): DeityAvatarDef | undefined {
  const canonical = TOOLID_ALIASES[toolId] ?? (toolId as DmtToolId | 'syqi');
  return DEITY_AVATARS[canonical];
}

/** Falls back to the existing SyQi GLB until a deity's real model is hosted (glbReady). */
export function resolveGlbUrl(toolId: string): string {
  const entry = getDeityAvatar(toolId);
  if (!entry || !entry.glbReady) return AVATAR_GLB_URL;
  return entry.glbUrl;
}
